/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
  WritableSignerAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const INITIALIZE_CONFIG_EXTENSION_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([55, 9, 53, 9, 114, 57, 209, 52]);

export function getInitializeConfigExtensionDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_CONFIG_EXTENSION_DISCRIMINATOR,
  );
}

export type InitializeConfigExtensionInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta = string,
  TAccountConfigExtension extends string | AccountMeta = string,
  TAccountFunder extends string | AccountMeta = string,
  TAccountFeeAuthority extends string | AccountMeta = string,
  TAccountSystemProgram extends
    | string
    | AccountMeta = "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountConfigExtension extends string
        ? WritableAccount<TAccountConfigExtension>
        : TAccountConfigExtension,
      TAccountFunder extends string
        ? WritableSignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountFeeAuthority extends string
        ? ReadonlySignerAccount<TAccountFeeAuthority> &
            AccountSignerMeta<TAccountFeeAuthority>
        : TAccountFeeAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export interface InitializeConfigExtensionInstructionData {
  discriminator: ReadonlyUint8Array;
}

export type InitializeConfigExtensionInstructionDataArgs = {};

export function getInitializeConfigExtensionInstructionDataEncoder(): FixedSizeEncoder<InitializeConfigExtensionInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_CONFIG_EXTENSION_DISCRIMINATOR,
    }),
  );
}

export function getInitializeConfigExtensionInstructionDataDecoder(): FixedSizeDecoder<InitializeConfigExtensionInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getInitializeConfigExtensionInstructionDataCodec(): FixedSizeCodec<
  InitializeConfigExtensionInstructionDataArgs,
  InitializeConfigExtensionInstructionData
> {
  return combineCodec(
    getInitializeConfigExtensionInstructionDataEncoder(),
    getInitializeConfigExtensionInstructionDataDecoder(),
  );
}

export interface InitializeConfigExtensionInput<
  TAccountConfig extends string = string,
  TAccountConfigExtension extends string = string,
  TAccountFunder extends string = string,
  TAccountFeeAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> {
  config: Address<TAccountConfig>;
  configExtension: Address<TAccountConfigExtension>;
  funder: TransactionSigner<TAccountFunder>;
  feeAuthority: TransactionSigner<TAccountFeeAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
}

export function getInitializeConfigExtensionInstruction<
  TAccountConfig extends string,
  TAccountConfigExtension extends string,
  TAccountFunder extends string,
  TAccountFeeAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: InitializeConfigExtensionInput<
    TAccountConfig,
    TAccountConfigExtension,
    TAccountFunder,
    TAccountFeeAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeConfigExtensionInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountConfigExtension,
  TAccountFunder,
  TAccountFeeAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    configExtension: { value: input.configExtension ?? null, isWritable: true },
    funder: { value: input.funder ?? null, isWritable: true },
    feeAuthority: { value: input.feeAuthority ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("config", accounts.config),
      getAccountMeta("configExtension", accounts.configExtension),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("feeAuthority", accounts.feeAuthority),
      getAccountMeta("systemProgram", accounts.systemProgram),
    ],
    data: getInitializeConfigExtensionInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeConfigExtensionInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountConfigExtension,
    TAccountFunder,
    TAccountFeeAuthority,
    TAccountSystemProgram
  >);
}

export interface ParsedInitializeConfigExtensionInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    configExtension: TAccountMetas[1];
    funder: TAccountMetas[2];
    feeAuthority: TAccountMetas[3];
    systemProgram: TAccountMetas[4];
  };
  data: InitializeConfigExtensionInstructionData;
}

export function parseInitializeConfigExtensionInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeConfigExtensionInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 5,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      configExtension: getNextAccount(),
      funder: getNextAccount(),
      feeAuthority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeConfigExtensionInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
